import React, { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";
import Sidebar from "./Sidebar";
import RefreshIcon from "@mui/icons-material/Refresh";
import "./vendor360.css";

function AdminMessages() {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/admin/messages/conversations");
      setConversations(res.data?.conversations || []);
      if (res.data?.conversations?.length > 0 && !activeConversation) {
        setActiveConversation(res.data.conversations[0]);
        fetchMessages(res.data.conversations[0]._id);
      }
    } catch (err) {
      console.warn("Error loading conversations:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await axios.get(`/api/admin/messages/${convId}`);
      setMessages(res.data?.messages || []);
      await axios.put(`/api/admin/messages/${convId}/read`).catch(() => {});
    } catch (err) {
      console.warn("Error loading messages:", err.message);
    }
  };

  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectConv = (conv) => {
    setActiveConversation(conv);
    fetchMessages(conv._id);
  };

  const handleSendReply = async (e) => {
    if (e) e.preventDefault();
    if (!replyText.trim() || !activeConversation) return;

    try {
      setSending(true);
      await axios.post("/api/admin/messages/send", {
        vendorId: activeConversation.vendor?._id || activeConversation.vendor,
        conversationId: activeConversation._id,
        message: replyText.trim(),
      });
      setReplyText("");
      fetchMessages(activeConversation._id);
      fetchConversations();
    } catch (err) {
      swal({
        title: "Send Failed",
        text: err.response?.data?.msg || "Could not send message.",
        icon: "error",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="main-container v360-main" style={{ padding: "24px", maxWidth: "1200px" }}>
        <header className="v360-header" style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h1 style={{ fontSize: "22px", fontWeight: 900, color: "#0f172a", margin: 0 }}>
                Vendor Communications &amp; Messages
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
                Direct support and compliance message threads with marketplace sellers
              </p>
            </div>
            <button
              type="button"
              className="v360-btn-secondary"
              onClick={fetchConversations}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <RefreshIcon style={{ fontSize: "16px" }} /> Refresh
            </button>
          </div>
        </header>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}>
            Loading conversations...
          </div>
        ) : conversations.length === 0 ? (
          <div className="v360-panel" style={{ textAlign: "center", padding: "60px" }}>
            <h3 style={{ color: "#0f172a", fontSize: "18px" }}>No Vendor Conversations Yet</h3>
            <p style={{ color: "#64748b", fontSize: "13px" }}>
              Messages initiated by sellers or sent from the Vendor 360 overview will appear here.
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: "20px", height: "calc(100vh - 200px)", minHeight: "500px" }}>
            {/* Conversations list */}
            <div className="v360-panel" style={{ padding: "0", overflowY: "auto", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <strong style={{ fontSize: "13px", color: "#334155" }}>
                  Active Threads ({conversations.length})
                </strong>
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                {conversations.map((conv) => (
                  <div
                    key={conv._id}
                    onClick={() => handleSelectConv(conv)}
                    style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid #f1f5f9",
                      cursor: "pointer",
                      background: activeConversation?._id === conv._id ? "#f0fdf4" : "#fff",
                      borderLeft: activeConversation?._id === conv._id ? "3px solid #16a34a" : "3px solid transparent",
                      transition: "all 0.15s",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>
                        {conv.vendor?.firstName ? `${conv.vendor.firstName} ${conv.vendor.lastName || ""}`.trim() : "Seller"}
                      </strong>
                      {conv.adminUnreadCount > 0 && (
                        <span style={{ background: "#dc2626", color: "#fff", fontSize: "11px", fontWeight: 800, padding: "2px 7px", borderRadius: "99px" }}>
                          {conv.adminUnreadCount}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {conv.lastMessage || "No messages yet"}
                    </p>
                    <span style={{ fontSize: "11px", color: "#94a3b8", display: "block", marginTop: "4px" }}>
                      {new Date(conv.lastMessageAt || conv.createdAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Message thread */}
            {activeConversation ? (
              <div className="v360-panel" style={{ padding: "0", display: "flex", flexDirection: "column", height: "100%" }}>
                <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <strong style={{ fontSize: "14.5px", color: "#0f172a" }}>
                      {activeConversation.vendor?.firstName ? `${activeConversation.vendor.firstName} ${activeConversation.vendor.lastName || ""}`.trim() : "Seller"}
                    </strong>
                    <span style={{ display: "block", fontSize: "12px", color: "#64748b" }}>
                      {activeConversation.vendor?.email || ""}
                    </span>
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "18px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  {messages.map((msg) => (
                    <div
                      key={msg._id}
                      style={{
                        alignSelf: msg.senderRole === "admin" ? "flex-end" : "flex-start",
                        maxWidth: "75%",
                        background: msg.senderRole === "admin" ? "#1e40af" : "#f1f5f9",
                        color: msg.senderRole === "admin" ? "#ffffff" : "#0f172a",
                        borderRadius: "14px",
                        padding: "12px 16px",
                        fontSize: "13.5px",
                        lineHeight: 1.5,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      }}
                    >
                      {msg.subject && (
                        <strong style={{ display: "block", marginBottom: "4px", fontSize: "12.5px" }}>
                          {msg.subject}
                        </strong>
                      )}
                      <p style={{ margin: 0 }}>{msg.message}</p>
                      <span style={{ fontSize: "10.5px", opacity: 0.75, display: "block", marginTop: "6px", textAlign: "right" }}>
                        {new Date(msg.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} &middot; {new Date(msg.createdAt).toLocaleDateString("en-IN", { dateStyle: "short" })}
                      </span>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSendReply} style={{ borderTop: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", gap: "10px", background: "#ffffff" }}>
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your response to the seller..."
                    style={{ flex: 1, padding: "10px 14px", border: "1.5px solid #cbd5e1", borderRadius: "8px", fontSize: "13.5px", outline: "none" }}
                  />
                  <button
                    type="submit"
                    disabled={sending || !replyText.trim()}
                    className="btn btn-primary"
                    style={{ padding: "10px 22px", fontWeight: 700, borderRadius: "8px" }}
                  >
                    {sending ? "Sending..." : "Send Reply"}
                  </button>
                </form>
              </div>
            ) : (
              <div className="v360-panel" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>
                Select a conversation on the left to read and reply.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminMessages;
